import React, {
  createContext,
  useContext,
  Children,
  cloneElement,
  type ReactElement,
  useEffect
} from 'react';

import {
  CompletedProps,
  StepperContextProps,
  StepperProps,
  StepProps,
} from '@/components/stepper/types';

import { Circle } from '@/components/stepper/circle';
import { cn } from '@/common/utils/clsx';
import { useBiometricSessionCheck } from '@/common/hooks/query';
import { useNotification } from '@/components/notification';
import { Loading } from '@hotmart-org-ca/user-account-ui';
import { useLoading } from '@/common/contexts/loading-context';
import { saveOnStorage } from '@/common/utils/general';
import { UCODE_KEY } from '@/common/constants';

export const StepperContext = createContext<StepperContextProps>({
  active: 0,
  onStepClick: () => {},
});

function Stepper({ children, active, onStepClick }: StepperProps) {
  const { hasError, setError } = useNotification();
  const { refetch, isLoading: isLoadingSessionCheck, isError, data: sessionData } = useBiometricSessionCheck(active, hasError);
  const { isLoading } = useLoading();

  const steps = Children.toArray(children).filter(
    (child) => (child as ReactElement).type !== Stepper.Completed,
  );
  const completedStep = Children.toArray(children).find(
    (child) => (child as ReactElement).type === Stepper.Completed,
  );

  useEffect(() => {
    if (sessionData?.ucode) {
      const storedUcode = localStorage.getItem(UCODE_KEY);
      if (storedUcode !== sessionData.ucode) {
        saveOnStorage({ key: UCODE_KEY, value: sessionData.ucode });
      }
    }
  }, [sessionData]);

  useEffect(() => {
    refetch();
  }, [active]);

  useEffect(() => {
    if (isError) {
      setError('authorization');
    }
  }, [isError]);

  return (
    <StepperContext.Provider value={{ active, onStepClick }}>
      <div className="flex flex-col">
        <div className="mb-4 flex">
          {steps.map((step, index) => (
            <React.Fragment key={`stepper-${index}`}>
              {cloneElement(step as ReactElement, { step: index })}
              {index < steps.length - 1 && (
                <div
                  className={`mx-2 my-auto h-px flex-1 ${index < active ? 'bg-blue' : 'bg-gray-300'}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-4">
          {(isLoading || isLoadingSessionCheck) && !isError
            ? <div className="flex h-52 items-center justify-center"><Loading /></div>
            : isError
              ? null
              : active < steps.length
                ? (steps[active] as ReactElement).props.children
                : completedStep}
        </div>
      </div>
    </StepperContext.Provider>
  );
}

function Step({ label, description, step = 0 }: StepProps) {
  const { active } = useContext(StepperContext);
  const isActive = step === active;
  const isCompleted = step < active;
  const status = isActive ? 'current' : isCompleted ? 'completed' : 'awaiting';

  return (
    <div className="flex flex-col items-center">
      <Circle
        status={status}
        tabIndex={step}
      />
      {label ||
        (description && (
          <div className="mt-2 text-center">
            {label && <div className="text-sm">{label}</div>}
            {label && description && (
              <div className="text-sm text-gray-500">{description}</div>
            )}
          </div>
        ))}
    </div>
  );
}

function Completed({ children, className }: CompletedProps) {
  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center bg-white', className)}>
      {children}
    </div>
  );
}

Step.displayName = 'Stepper.Step';

Stepper.displayName = 'Stepper';
Stepper.Step = Step;

Stepper.Completed = Completed;
Completed.Completed = 'Stepper.Completed';

export default Stepper;
